import {
  currencySchema,
  HyperPayCheckoutResponse,
  idSchema,
  imageWithUploadSchema,
  Invoice,
  objectIdSchema,
  paymentMethodsHyperPaySchema,
  priceSchema,
} from '@hero/schema';
import { z } from 'zod';
import { API, PaymentSuccessData } from '../..';

//* create
export const PaymentCreateOrderInvoiceAPISchema = z.object({
  /** the order id for which we need to create the invoice for.
   *
   * We will get the total amounts from the order itself.
   */
  orderId: objectIdSchema,
});

/** this API will be used to create invoice only.
 *
 * creating an invoice does `NOT` mean the order has been paid.
 *
 * Invoices are `only` used to `acknowledge` the amounts and add them into the customer/carrier account as due
 */
export type PaymentCreateOrderInvoiceAPI = API<
  z.infer<typeof PaymentCreateOrderInvoiceAPISchema>,
  Invoice
>;

/** Item in the `invoices` array — uses an object so the schema is extensible (e.g. per-invoice amount in the future). */
export const paymentInvoiceItemSchema = z.object({
  _id: objectIdSchema,
});
export type PaymentInvoiceItem = z.infer<typeof paymentInvoiceItemSchema>;

/** Schema for creating a payment via Hyperpay */
export const CreatePaymentAPISchema = z.object({
  /** The amount to be paid */
  amount: priceSchema,
  /** The currency code */
  currency: currencySchema,
  /** Payment method */
  paymentMethod: paymentMethodsHyperPaySchema,

  /** Targeted invoices (same structure as bank transfer). */
  invoices: z.array(paymentInvoiceItemSchema).min(1).optional(),

  /** Targeted order ID */
  orderId: objectIdSchema.optional(),
});

/** API type for creating a payment */
export type CreatePaymentAPI = API<
  z.infer<typeof CreatePaymentAPISchema>,
  HyperPayCheckoutResponse
>;

//* status
export const getPaymentStatusAPISchema = z.object({
  /** The checkout ID */
  checkoutId: idSchema,
  /** The payment method */
  paymentMethod: paymentMethodsHyperPaySchema,
});

export type GetPaymentStatusAPI = API<
  z.infer<typeof getPaymentStatusAPISchema>,
  any // Replace with the actual response type if known
>;

/** Base object schema for bank transfer payments.
 *  Exported so other schemas (e.g. `invoicePayAdminAPISchema`) can `.extend()` it.
 *
 * Target resolution (mutually exclusive):
 * - `invoices` with 1 item  → single invoice payment (`invoice_id`)
 * - `invoices` with 2+ items → multi-invoice payment (`multi_invoice`)
 * - `orderId` provided      → single order payment (`order_id`)
 * - neither                  → advance payment / wallet deposit (`advance_payment`)
 */
export const createPaymentWithBankTransferBaseSchema = z.object({
  /** The amount to be paid */
  amount: priceSchema,
  /** The currency code */
  currency: currencySchema,
  /** Proof of transaction receipt(s) uploaded by the user. */
  proofOfTransaction: z.array(imageWithUploadSchema).min(1).max(5),

  /** Targeted invoices — mutually exclusive with `orderId`.
   *
   * - 1 item  → single invoice payment
   * - 2+ items → multi-invoice payment
   * - omitted  → advance payment (when `orderId` is also omitted)
   */
  invoices: z.array(paymentInvoiceItemSchema).min(1).optional(),

  /** Targeted order — mutually exclusive with `invoices`.
   *  The backend resolves which invoice the order belongs to and only marks this specific order as paid.
   */
  orderId: objectIdSchema.optional(),
});

/** Refined schema with mutual-exclusivity validation. Used for controller validation. */
export const createPaymentWithBankTransferAPISchema =
  createPaymentWithBankTransferBaseSchema.refine(
    data => !(data.invoices && data.orderId),
    { message: 'Cannot provide both invoices and orderId — they are mutually exclusive' },
  );

export type CreatePaymentWithBankTransferAPI = API<
  z.infer<typeof createPaymentWithBankTransferAPISchema>,
  PaymentSuccessData
>;
